import type { ISchedulerData, ISchedulerTask } from '@interfaces/IScheduler';
import type {
  IProjectProgressHeadline,
  IProjectProgressPhase,
  ProjectProgressPhaseStatus,
} from '@interfaces/IProjectProgress';

/** Max main-activity rows before the rest fold into a single "other" row. */
export const MAX_PHASE_ROWS = 5;

const mean = (nums: number[]): number =>
  nums.length === 0 ? 0 : nums.reduce((a, b) => a + b, 0) / nums.length;

/** A task's expected progress today, read from its planned window. 0 before
 *  start, 100 on/after end, linear in between. Zero-duration / milestone
 *  (end <= start) is 100 once the date passes, else 0. Bad dates -> 0. */
export function expectedTaskProgress(task: ISchedulerTask, todayMs: number): number {
  const start = Date.parse(task.start);
  const end = Date.parse(task.end);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  if (todayMs >= end) return 100;
  if (todayMs <= start) return 0;
  if (end <= start) return 0; // safety; unreachable when end > start
  return ((todayMs - start) / (end - start)) * 100;
}

export function phaseStatus(pct: number): ProjectProgressPhaseStatus {
  if (pct >= 100) return 'complete';
  if (pct <= 0) return 'not-started';
  return 'in-progress';
}

/** Rounded completion of one group = the Scheduler's own group rollup
 *  (mean of task progressPercent). Mirror of
 *  SchedulerPage/components/RowLabels/RowLabels.tsx:62 — keep in sync. */
function groupCompletion(data: ISchedulerData, groupId: string): number {
  const pcts = data.tasks.filter((t) => t.groupId === groupId).map((t) => t.progressPercent);
  return Math.round(mean(pcts));
}

/** Phase rows = main activities, sorted by completion desc (tie-break: schedule
 *  order), top 5 shown, remainder folded into one "other" row. */
export function derivePhases(data: ISchedulerData): IProjectProgressPhase[] {
  const groups = data.groups.map((g, index) => ({
    group: g,
    index,
    pct: groupCompletion(data, g.id),
  }));
  const sorted = [...groups].sort((a, b) => b.pct - a.pct || a.index - b.index);

  const rows: IProjectProgressPhase[] = sorted.slice(0, MAX_PHASE_ROWS).map(({ group, pct }) => ({
    key: group.id,
    name: group.name,
    pct,
    status: phaseStatus(pct),
  }));

  const rest = sorted.slice(MAX_PHASE_ROWS);
  if (rest.length > 0) {
    const pct = Math.round(mean(rest.map((g) => g.pct)));
    rows.push({ key: 'other', name: '', pct, status: phaseStatus(pct) });
  }
  return rows;
}

/** Headline figures. Completion is the mean of each main activity's rollup;
 *  planned is the mean of each activity's expected-progress rollup; variance and
 *  SPI fall out. Groups with no tasks are excluded so they don't drag the means. */
export function deriveHeadline(data: ISchedulerData, todayMs: number): IProjectProgressHeadline {
  const groupTaskLists = data.groups
    .map((g) => data.tasks.filter((t) => t.groupId === g.id))
    .filter((tasks) => tasks.length > 0);

  const completions = groupTaskLists.map((tasks) =>
    Math.round(mean(tasks.map((t) => t.progressPercent))),
  );
  const planneds = groupTaskLists.map((tasks) =>
    Math.round(mean(tasks.map((t) => expectedTaskProgress(t, todayMs)))),
  );

  const overallPct = Math.round(mean(completions));
  const plannedPct = Math.round(mean(planneds));
  const variancePct = overallPct - plannedPct;
  const spi = plannedPct > 0 ? Math.round((overallPct / plannedPct) * 100) / 100 : null;

  return { overallPct, plannedPct, variancePct, spi };
}
